import {
	faChevronDown,
	faSpinner,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import CustomMonacoEditor from "@/components/CustomMonacoEditor";
import { useToast } from "@/components/common/Toast";
import { useAuthStore } from "@/store/auth.store";
import { FlowAction } from "@/store/flowActions.store";
import { isValidBase64 } from "@/utils/base64";
import { ApiError, handleApiError } from "@/utils/errorHandler";

const FLOW_ACTIONS_QUERY_KEY = ["flow-actions"] as const;

interface FlowTarget {
	id: string;
	name: string;
	status: string;
	credentialSchema: Record<string, unknown>;
	logo: string | null;
	inputSchema: Record<string, unknown>;
	createdAt: string;
	updatedAt: string;
	brandId?: string;
}

interface AddFlowTargetProps {
	flowTypeId: string;
	target?: FlowTarget | null;
	onClose: () => void;
}

const FLOW_TARGETS_QUERY_KEY = (flowTypeId: string) =>
	["flow-targets", flowTypeId] as const;

const getDefaultInputSchema = (flowActions: FlowAction[]) => {
	if (!flowActions) return {};

	return flowActions.reduce(
		(schema: Record<string, object>, flowAction: FlowAction) => {
			if (flowAction.name) {
				schema[flowAction.name] = {};
			}
			return schema;
		},
		{}
	);
};

const AddFlowTarget: React.FC<AddFlowTargetProps> = ({
	flowTypeId,
	target,
	onClose
}) => {
	const { success, error } = useToast();
	const queryClient = useQueryClient();
	const { brandId } = useAuthStore();

	const flowActions =
		(queryClient.getQueryData([
			...FLOW_ACTIONS_QUERY_KEY,
			flowTypeId
		]) as FlowAction[]) || [];

	const [formData, setFormData] = useState({
		name: target?.name || "",
		status: target?.status || "ENABLED",
		credentialSchema: target?.credentialSchema || {},
		logo: target?.logo || (null as string | null),
		inputSchema: target?.inputSchema || getDefaultInputSchema(flowActions)
	});

	useEffect(() => {
		if (flowActions && Object.keys(formData.inputSchema).length === 0) {
			setFormData((prev) => ({
				...prev,
				inputSchema: getDefaultInputSchema(flowActions)
			}));
		}
	}, [flowActions, formData]);

	const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				const base64String = reader.result as string;

				setFormData((prev) => ({ ...prev, logo: base64String }));
			};
			reader.readAsDataURL(file);
		}
	};

	const createTargetMutation = useMutation({
		mutationFn: async (data: typeof formData) => {
			const { data: response } = await apiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_TARGETS", "CREATE"], flowTypeId),
				{
					name: data.name,
					status: data.status,
					credentialSchema: data.credentialSchema,
					logo: data.logo ?? "logo",
					brandId,
					inputSchema: data.inputSchema
				}
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: FLOW_TARGETS_QUERY_KEY(flowTypeId)
			});
			success("Service provider created successfully");
			onClose();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to create service provider");
		}
	});

	const updateTargetMutation = useMutation({
		mutationFn: async ({
			targetId,
			data
		}: {
			targetId: string;
			data: typeof formData;
		}) => {
			const { data: response } = await apiClient.put(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_TARGETS", "UPDATE_BY_ID"],
					flowTypeId,
					targetId
				),
				{
					name: data.name,
					logo: data.logo && isValidBase64(data.logo) ? data.logo : "logo",
					status: data.status,
					credentialSchema: data.credentialSchema,
					inputSchema: data.inputSchema
				}
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: FLOW_TARGETS_QUERY_KEY(flowTypeId)
			});
			success("Service provider updated successfully");
			onClose();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update service provider");
		}
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.name.trim()) {
			error("Name is required");
			return;
		}

		if (target) {
			updateTargetMutation.mutate({
				targetId: target.id,
				data: formData
			});
		} else {
			createTargetMutation.mutate(formData);
		}
	};

	const renderLogoPreview = (logo: string | null) => {
		if (!logo) return null;

		try {
			const isValidImage = isValidBase64(logo);

			if (!isValidImage) {
				return (
					<div className="mt-4 border rounded-lg p-4 bg-gray-50">
						<div className="flex justify-center">
							<img
								src="/assets/images/preview-not-found.png"
								alt="Preview not available"
								className="max-h-48 max-w-full object-contain opacity-50"
							/>
						</div>
					</div>
				);
			}

			return (
				<div className="mt-4 border rounded-lg p-4 bg-gray-50">
					<div className="flex justify-center">
						<img
							src={logo}
							alt="Target Logo Preview"
							className="max-h-48 max-w-full object-contain"
							onError={(e) => {
								e.currentTarget.src = "/assets/images/preview-not-found.png";
								e.currentTarget.className =
									"max-h-48 max-w-full object-contain opacity-50";
							}}
						/>
					</div>
				</div>
			);
		} catch (_error) {
			return (
				<div className="mt-4 border rounded-lg p-4 bg-gray-50">
					<div className="flex justify-center">
						<img
							src="/assets/images/preview-not-found.png"
							alt="Preview not available"
							className="max-h-48 max-w-full object-contain opacity-50"
						/>
					</div>
				</div>
			);
		}
	};

	const isLoading =
		createTargetMutation.isPending || updateTargetMutation.isPending;

	return (
		<div className="space-y-6 h-full">
			<form
				onSubmit={handleSubmit}
				className="bg-white flex flex-col h-full max-w-md mx-auto rounded-lg"
			>
				<div className="mb-4 flex-1 space-y-4">
					<div>
						<label
							htmlFor="target-name"
							className="block text-sm font-medium text-gray-700"
						>
							Name
						</label>
						<input
							type="text"
							value={formData.name}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									name: e.target.value
								}))
							}
							disabled={isLoading}
							className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
							placeholder="Enter target name"
						/>
					</div>

					<div>
						<label
							htmlFor="target-logo"
							className="block text-sm font-medium text-gray-700 w-full"
						>
							Logo
						</label>
						<div className="mt-1 space-y-4">
							<div className="flex items-center justify-between space-x-2">
								<label className="w-full relative cursor-pointer bg-white rounded-md font-medium text-blue-fynxt hover:text-blue-700 focus-within:outline-none">
									<span className="w-full inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
										{formData.logo ? "Change Logo" : "Upload Logo"}
									</span>
									<input
										type="file"
										className="sr-only"
										onChange={handleLogoChange}
										accept="image/*"
										disabled={isLoading}
									/>
								</label>
								{formData.logo && (
									<button
										type="button"
										onClick={() =>
											setFormData((prev) => ({
												...prev,
												logo: null
											}))
										}
										disabled={isLoading}
										className="text-sm text-red-600 hover:text-red-900 p-1"
									>
										<FontAwesomeIcon icon={faTrash} />
									</button>
								)}
							</div>
							{formData.logo && renderLogoPreview(formData.logo)}
							<p className="text-sm text-gray-500">PNG, JPG, GIF up to 5MB</p>
						</div>
					</div>

					{target && (
						<div>
							<label
								htmlFor="target-status"
								className="block text-sm font-medium text-gray-700"
							>
								Status
							</label>
							<div className="relative w-full">
								<select
									value={formData.status}
									onChange={(e) =>
										setFormData((prev) => ({
											...prev,
											status: e.target.value
										}))
									}
									disabled={isLoading}
									className="appearance-none mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
								>
									<option value="ENABLED">Enabled</option>
									<option value="DISABLED">Disabled</option>
								</select>
								<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
									<FontAwesomeIcon
										icon={faChevronDown}
										className="text-gray-500 text-sm"
									/>
								</div>
							</div>
						</div>
					)}

					<div>
						<label
							htmlFor="target-config"
							className="block text-sm font-medium text-gray-700"
						>
							Credential Schema
						</label>
						<div className="mt-1 border rounded-md overflow-hidden">
							<CustomMonacoEditor
								value={JSON.stringify(formData.credentialSchema, null, 2)}
								onChange={(value) => {
									try {
										const parsed = JSON.parse(value || "{}");
										setFormData((prev) => ({
											...prev,
											credentialSchema: parsed
										}));
									} catch (_error) {
										// Handle invalid JSON
									}
								}}
								height="250px"
								language="json"
							/>
						</div>
					</div>

					<div>
						<label
							htmlFor="target-config"
							className="block text-sm font-medium text-gray-700"
						>
							Input Schema
						</label>
						<div className="mt-1 border rounded-md overflow-hidden">
							<CustomMonacoEditor
								value={JSON.stringify(formData.inputSchema, null, 2)}
								onChange={(value) => {
									try {
										const parsed = JSON.parse(
											value ||
												JSON.stringify(getDefaultInputSchema(flowActions))
										);
										setFormData((prev) => ({
											...prev,
											inputSchema: parsed
										}));
									} catch (_error) {
										// Handle invalid JSON
									}
								}}
								height="250px"
								language="json"
							/>
						</div>
					</div>
				</div>
				<div className="py-4 bg-white mb-[15px]">
					<button
						type="submit"
						disabled={isLoading}
						className="w-full bg-blue-fynxt text-white py-2 px-4 text-sm rounded-md hover:bg-blue-700 transition-colors"
					>
						{isLoading ? (
							<>
								<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
								{target ? "Updating..." : "Creating..."}
							</>
						) : target ? (
							"Update Service Provider"
						) : (
							"Create Service Provider"
						)}
					</button>
				</div>
			</form>
		</div>
	);
};

export default AddFlowTarget;
